import { shallowRef, markRaw, defineAsyncComponent } from "vue";
import type {
  ConditionalRuleAction,
  ConditionStateField,
} from "../lib/conditionalRules";

export interface BlockDefinition {
  id: string
  component?: any
  loader?: () => Promise<any>
  editor?: () => Promise<any>
  icon?: string
  type?: string
  description?: string
  default?: any
  categories?: string[]
  hiddenUntilFiltered?: boolean
  filter?: string[]
  order?: number
  /**
   * Optional per-block overrides for the inline editor panel sections.
   * Keyed by section key ("identity" | "content" | "validation" | "layout"
   * | "rules" | "raw"); each entry may override the section's `order`
   * (lower = higher up) and/or its `defaultOpen` (expanded) state.
   */
  editorSections?: Record<string, { order?: number; defaultOpen?: boolean }>
  conditionStateFields?: ConditionStateField[]
  conditionActions?: ConditionalRuleAction[]
}

const registry = shallowRef<Record<string, BlockDefinition>>({});
const cache = shallowRef<Record<string, any>>({});
const editorCache = shallowRef<Record<string, any>>({});

export const useBlockRegistry = () => {
  const registerBlock = (block: BlockDefinition) => {
    const safeBlock: BlockDefinition = {
      ...block,
      component: block.component ? markRaw(block.component) : undefined,
      loader: block.loader ? markRaw(block.loader) : undefined,
      editor: block.editor ? markRaw(block.editor) : undefined,
    };
    registry.value[safeBlock.id] = safeBlock;
  };

  const getBlock = (id: string): BlockDefinition | undefined => {
    return registry.value[id];
  };

  const listBlocks = (): BlockDefinition[] => {
    return Object.values(registry.value);
  };

  const normalize = (val: string) => (val.startsWith('/') ? `..${val}` : val);

  const resolveBlockComponent = (id: string) => {
    const key = normalize(id);

    if (cache.value[key]) {
      return cache.value[key];
    }

    const block = registry.value[key];
    if (!block) {
      console.warn(`No component registered for id: ${id}`);
      return null;
    }

    if (block.component) {
      cache.value[key] = block.component;
      return block.component;
    }

    if (block.loader) {
      const asyncComp = defineAsyncComponent({
        loader: async () => {
          const mod = await block.loader!();
          return markRaw(mod.default || mod);
        },
        delay: 200,
        timeout: 3000,
      });
      cache.value[key] = asyncComp;
      return asyncComp;
    }

    console.warn(`Block ${id} has no component or loader defined`);
    return null;
  };

  const resolveEditorComponent = (id: string) => {
    const key = normalize(id);

    if (editorCache.value[key]) {
      return editorCache.value[key];
    }

    const block = registry.value[key];
    if (!block) {
      console.warn(`No block registered for editor lookup id: ${id}`);
      return null;
    }

    if (!block.editor) {
      return null;
    }

    const asyncComp = defineAsyncComponent({
      loader: async () => {
        const mod = await block.editor!();
        return markRaw(mod.default || mod);
      },
      delay: 150,
    });
    editorCache.value[key] = asyncComp;
    return asyncComp;
  };

  return { registerBlock, getBlock, listBlocks, resolveBlockComponent, resolveEditorComponent };
};
